import React from 'react';
import { Link, useLocation } from 'react-router-dom';

/* =========================================================
   Marketing-site design tokens.

   Single source of truth for the four /site pages (Home, Customer AI
   Platform, Teammate, Orbiter) and the shell in DemoSiteLayout. Import
   these instead of writing utility strings inline — that is what let the
   pages drift apart (Home on blue at 46px headings, the product pages on
   teal at 42px, four different card recipes).

   Accent is Tailwind `teal` to match the dashboard (src/pages/demo/ui.js)
   and the brand guide's #459EA3 family.
   ========================================================= */

/* ---------- type scale ----------
   Deliberate ratio rather than ad-hoc bracket values. Display sizes get
   negative tracking; body copy stays at default tracking for legibility. */
export const T = {
  display: 'text-[44px] font-bold leading-[1.06] tracking-[-0.022em] text-balance',
  h2: 'text-[28px] font-bold leading-[1.15] tracking-[-0.018em]',
  h3: 'text-[19px] font-bold leading-snug tracking-[-0.01em]',
  /* Hero sub-headline — the bold statement under an h1. */
  deck: 'text-[21px] font-bold leading-[1.25] tracking-[-0.012em]',
  lede: 'text-[15.5px] leading-[1.6] text-slate-500 text-pretty',
  body: 'text-[13.5px] leading-[1.65] text-slate-600',
  micro: 'text-[12px] leading-[1.5] text-slate-500',
};

/* ---------- elevation ----------
   Two-stop shadow: a tight contact shadow to seat the element, plus a wide
   soft ambient. Reads closer to real light than a single flat `shadow-sm`. */
export const SHADOW = {
  card: 'shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.14)]',
  raised: 'shadow-[0_2px_4px_rgba(15,23,42,0.05),0_16px_40px_-16px_rgba(15,23,42,0.2)]',
  accent: 'shadow-[0_1px_2px_rgba(15,23,42,0.08),0_8px_20px_-10px_rgba(13,148,136,0.55)]',
};

/* Hairline via ring rather than `border`, so it never adds to layout size. */
export const HAIRLINE = 'ring-1 ring-slate-900/5';

/* ---------- surfaces ----------
   Radius hierarchy, largest container to smallest element:
   band 28 → card 16 (2xl) → chip 12 (xl) → pill full.

   NOTE: every Tailwind class below is written out in full. Tailwind's JIT
   scanner regexes the raw source, so a class assembled by interpolation
   (`hover:${SHADOW.raised}`) is never generated. Keep variants literal. */
export const SURFACE = {
  band: `rounded-[28px] bg-white ${HAIRLINE} ${SHADOW.card}`,
  card: `rounded-2xl bg-white ${HAIRLINE} ${SHADOW.card}`,
  cardHover:
    'rounded-2xl bg-white ring-1 ring-slate-900/5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.14)] transition-shadow duration-200 hover:shadow-[0_2px_4px_rgba(15,23,42,0.05),0_16px_40px_-16px_rgba(15,23,42,0.2)]',
  chip: `rounded-xl bg-white ${HAIRLINE} ${SHADOW.card}`,
};

const FOCUS =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600';

/* ---------- buttons ---------- */

export const PrimaryButton = ({ children, className = '', ...props }) => (
  <button
    className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-teal-600 px-6 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-teal-700 ${SHADOW.accent} ${FOCUS} ${className}`}
    {...props}
  >
    {children}
  </button>
);

export const GhostButton = ({ children, className = '', ...props }) => (
  <button
    className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-white px-6 py-3 text-[14px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 ${HAIRLINE} ${SHADOW.card} ${FOCUS} ${className}`}
    {...props}
  >
    {children}
  </button>
);

/** "Book a demo", styled as the primary button but rendered as a link. Sales
 *  needs to know which page prompted the request, so the current path rides
 *  along in ?from=. */
export const DemoLink = ({ children = 'Book a demo', className = '', ...props }) => {
  const { pathname } = useLocation();
  return (
    <Link
      to={`/site/book-a-demo?from=${encodeURIComponent(pathname)}`}
      className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-teal-600 px-6 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-teal-700 ${SHADOW.accent} ${FOCUS} ${className}`}
      {...props}
    >
      {children}
    </Link>
  );
};

/* ---------- page furniture ---------- */

/** Small teal marker above an h1. Sentence case — an all-caps eyebrow reads
 *  as template chrome, and the four pages previously disagreed about it. */
export const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-teal-700">
    <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />
    {children}
  </span>
);

/** Hero block shared by all four pages: eyebrow, h1, optional deck, lede, actions. */
export const Hero = ({ eyebrow, title, deck, children, actions, footerLead, footer }) => (
  <div>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <h1 className={`${eyebrow ? 'mt-4' : ''} ${T.display}`}>{title}</h1>
    {deck && <p className={`mt-3 ${T.deck}`}>{deck}</p>}
    {/* ~52ch keeps the lede close to the previous max-w-md measure and well
        under the 80-char comfortable-reading ceiling. */}
    {children && <p className={`mt-4 max-w-[52ch] ${T.lede}`}>{children}</p>}
    {actions && <div className="mt-8 flex flex-wrap items-center gap-3">{actions}</div>}
    {/* Sits between the CTAs and the supporting statement — used for the
        sample-call player, which is an action but not a button. */}
    {footerLead && <div className="mt-6">{footerLead}</div>}
    {/* Supporting claim under the CTAs — fills the column so it does not
        bottom out well above the hero graphic beside it. */}
    {footer && <div className="mt-10 border-t border-slate-200/70 pt-6">{footer}</div>}
  </div>
);

/** A white section band. `flush` drops the inner padding for custom layouts. */
export const Band = ({ title, subtitle, children, className = '', flush }) => (
  <section className={`${SURFACE.band} ${flush ? '' : 'px-6 py-10 md:px-10'} ${className}`}>
    {title && <h2 className={`text-center ${T.h2}`}>{title}</h2>}
    {subtitle && <p className={`mx-auto mt-3 max-w-[54ch] text-center ${T.lede}`}>{subtitle}</p>}
    {children}
  </section>
);

/** Rounded icon tile. `tint` carries meaning (channel, department), so the
 *  hue stays variable while the shape and weight stay fixed. */
export const IconTile = ({ icon: Icon, tint = 'bg-teal-50 text-teal-600', size = 'md' }) => {
  const box = size === 'lg' ? 'h-14 w-14' : size === 'sm' ? 'h-9 w-9' : 'h-12 w-12';
  const glyph = size === 'lg' ? 23 : size === 'sm' ? 16 : 20;
  return (
    <span className={`flex ${box} shrink-0 items-center justify-center rounded-xl ${tint}`}>
      <Icon size={glyph} strokeWidth={1.85} />
    </span>
  );
};

/** Numbered process step. Only for genuine sequences (Teammate/Orbiter "how it works"). */
export const StepMarker = ({ n }) => (
  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-600 text-[14px] font-bold text-white">
    {n}
  </span>
);

/** Headline figure over a caption. Used for the stat strips. */
export const StatFigure = ({ value, label, className = '' }) => (
  <div className={`text-center ${className}`}>
    <p className="text-[32px] font-bold tracking-[-0.02em] text-teal-600">{value}</p>
    <p className={`mx-auto mt-2 max-w-[22ch] ${T.micro}`}>{label}</p>
  </div>
);


/* ---------- template pieces (marketing site) ----------
   The landing mockup establishes four repeating devices. Product pages use the
   same ones so the site reads as a set. */

/** Tinted statement band: a single large claim on a soft ground, used once per
 *  page to break up the white sections. */
export const StatementBand = ({ children, sub }) => (
  <section className="rounded-[28px] bg-teal-50/40 px-6 py-12 text-center md:px-10">
    <p className="mx-auto max-w-[24ch] text-[30px] font-bold leading-[1.2] tracking-[-0.02em] text-balance">
      {children}
    </p>
    {sub && <p className={`mx-auto mt-4 max-w-[52ch] ${T.lede}`}>{sub}</p>}
  </section>
);

/** Centred section heading with a supporting line. The mockup centres every
 *  section head, so this is the default on this site. */
export const CenterHead = ({ title, subtitle }) => (
  <div className="text-center">
    <h2 className={T.h2}>{title}</h2>
    {subtitle && <p className={`mx-auto mt-3 max-w-[64ch] ${T.lede}`}>{subtitle}</p>}
  </div>
);

/** Figures separated by vertical rules rather than boxed in cards. */
export const StatStrip = ({ stats }) => (
  <div className="grid grid-cols-2 gap-y-8 lg:grid-cols-4">
    {stats.map(({ value, label }, i) => (
      <div
        key={label}
        className={`px-4 text-center ${i > 0 ? 'lg:border-l lg:border-slate-200/80' : ''} ${
          i % 2 === 1 ? 'border-l border-slate-200/80 lg:border-l' : ''
        }`}
      >
        <p className={`text-[26px] text-teal-600 ${T.figure}`}>{value}</p>
        <p className={`mx-auto mt-2 max-w-[20ch] ${T.micro}`}>{label}</p>
      </div>
    ))}
  </div>
);

/** Line-icon strip with vertical dividers — the "built for impact across
 *  industries" device. Icons stay monochrome; the label carries the meaning. */
export const IconStrip = ({ items }) => (
  <div className="grid grid-cols-2 gap-y-7 sm:grid-cols-4 lg:grid-cols-7">
    {items.map(({ name, icon: Icon }, i) => (
      <div
        key={name}
        className={`flex flex-col items-center gap-2.5 px-2 text-center ${
          i > 0 ? 'lg:border-l lg:border-slate-200/80' : ''
        }`}
      >
        <Icon size={26} strokeWidth={1.4} className="text-slate-700" />
        <span className="text-[12px] font-semibold leading-tight">{name}</span>
      </div>
    ))}
  </div>
);

/** Small labelled chip used inside the product cards. */
export const Chip = ({ icon: Icon, children }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-xl bg-white px-3 py-2 text-[12px] font-semibold ${HAIRLINE}`}>
    {Icon && <Icon size={13} strokeWidth={2} className="text-teal-600" />}
    {children}
  </span>
);

const siteUI = {
  T,
  DemoLink,
  StatementBand,
  CenterHead,
  StatStrip,
  IconStrip,
  Chip,
  SHADOW,
  HAIRLINE,
  SURFACE,
  PrimaryButton,
  GhostButton,
  Eyebrow,
  Hero,
  Band,
  IconTile,
  StepMarker,
  StatFigure,
};

export default siteUI;
